/**
 * CouponDetailsModal - Dynamic modal component for displaying coupon combination details
 * 
 * This component automatically renders all fields from the combination data.
 * To add new fields:
 * 1. Add the field to the appropriate category in fieldCategories
 * 2. If it's a selectable field, add it to shouldUseRenderSavedField
 * 3. If it needs special styling, add it to renderFieldValue
 * 4. Unknown fields will automatically appear in "Additional Information" section
 */

import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Divider, StackView, Spacer, Typography } from '@telus-uds/components-web';
import { useLocalizedStringsContext } from '../../../context/LocalizedStringsContext';
import { useLocaleContext } from '../../../context/LocaleContext';
import { FIELD_NAME_TO_TRANSLATION_KEY, FIELD_CATEGORIES, BILINGUAL_FIELDS } from './constants';
import {
  Backdrop,
  ModalMain,
  ModalInner,
  ModalMessageMain,
  ModalCancel,
  ModalHeading,
  NavbarPopup,
  ModalHeadingStyled,
  ModalButton
} from './css';

const REFERENCE_FIELD_SOURCES = { channel: 'channels', dealership: 'dealerships' };

const CouponDetailsModal = ({ 
  isOpen, 
  combination, 
  referenceOptions,
  onClose
}) => {
  if (!isOpen || !combination) return null;
  
  const localizedStrings = useLocalizedStringsContext();
  const { COUPON_CODES } = localizedStrings;
  const locale = useLocaleContext();
  const isFrench = locale === 'fr-CA';

  // Helper function to get nested translation values
  const getTranslation = (keyPath) => {
    const keys = keyPath.split('.');
    let value = COUPON_CODES;
    keys.forEach(key => {
      value = value && value[key];
    });
    return value || keyPath;
  };

  // Helper function to parse bilingual string and get display value based on locale
  const getDisplayValueFromBilingualString = (bilingualString) => {
    if (!bilingualString) return '';
    const str = String(bilingualString);
    const arr = str.split(', ');
    let out = [];
    for (let i = 0; i < arr.length; i += 2) {
      if (arr[i] && arr[i + 1]) {
        out.push(isFrench ? arr[i + 1] : arr[i]);
      }
    }
    return out.join(', ');
  };

  // Create field categories with translated titles
  const fieldCategories = {
    couponInfo: {
      title: getTranslation(FIELD_CATEGORIES.coupon.titleKey),
      fields: FIELD_CATEGORIES.coupon.fields
    },
    configuration: {
      title: getTranslation(FIELD_CATEGORIES.selection.titleKey),
      fields: FIELD_CATEGORIES.selection.fields
    },
    bilingual: {
      title: getTranslation(FIELD_CATEGORIES.bilingual.titleKey),
      fields: FIELD_CATEGORIES.bilingual.fields
    },
    contract: {
      title: getTranslation(FIELD_CATEGORIES.contract.titleKey),
      fields: FIELD_CATEGORIES.contract.fields
    }
  };

  // Helper function to get translated field name
  const getTranslatedFieldName = (fieldName) => {
    const translationKey = FIELD_NAME_TO_TRANSLATION_KEY[fieldName];
    if (translationKey) {
      return getTranslation(translationKey);
    }
    
    // Fallback to formatted field name for unknown fields
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .replace(/En$/, ' (English)')
      .replace(/Fr$/, ' (French)');
  };

  // Helper function to safely convert value to string
  const valueToString = (value, fieldName) => {
    // Only show dash for truly empty/null/undefined values
    if (value === null || value === undefined) return '-';
    
    // Format dates to show only date (no time)
    const dateFields = ['startDate', 'expireDate', 'createdAt', 'updatedAt'];
    if (dateFields.includes(fieldName)) {
      return moment(value).format('YYYY-MM-DD');
    }
    
    if (typeof value === 'string') {
      const trimmed = value.trim();
      // Only show dash for empty strings, not for "0" which could be a valid value
      return trimmed === '' ? '-' : trimmed;
    }
    
    if (typeof value === 'number') {
      // Show the actual number, including 0 if it was entered
      return value.toString();
    }
    
    if (typeof value === 'boolean') return value.toString();
    
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '-';
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    
    return String(value);
  };

  // Helper function to render a field value
  const renderFieldValue = (fieldName, value) => {
    let displayValue = value;
    
    // Process bilingual fields to show only locale-specific value
    if (BILINGUAL_FIELDS.includes(fieldName) && value) {
      displayValue = getDisplayValueFromBilingualString(String(value));
    }

    // Channel / dealership answers are stored as IDs; show their names
    const referenceSource = REFERENCE_FIELD_SOURCES[fieldName];
    if (referenceSource && value) {
      const options = (referenceOptions && referenceOptions[referenceSource]) || [];
      displayValue = String(value).split(',').map(id => id.trim()).filter(Boolean)
        .map(id => {
          const match = options.find(opt => opt.value === id);
          return match ? match.label : `#${id}`;
        })
        .join(', ');
    }
    
    // Special styling for textarea fields
    if (fieldName === 'commentInContract') {
      return (
        <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', minHeight: '80px' }}>
          {valueToString(displayValue, fieldName)}
        </div>
      );
    }
    
    // All other fields use the same styling with consistent height
    return (
      <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', minHeight: '36px', display: 'flex', alignItems: 'center' }}>
        {valueToString(displayValue, fieldName)}
      </div>
    );
  };

  // Helper function to render a category section
  const renderCategorySection = (categoryKey) => {
    const category = fieldCategories[categoryKey];
    // Show all fields that exist in the combination, even if empty
    const fieldsWithValues = category.fields.filter(field => 
      combination && Object.prototype.hasOwnProperty.call(combination, field)
    );
    
    if (fieldsWithValues.length === 0) return null;

    return (
      <React.Fragment key={categoryKey}>
        <StackView space={3}>
          <Typography heading="h4" variant={{ size: 'h4' }} block>{category.title}</Typography>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {fieldsWithValues.map(fieldName => (
              <div key={fieldName}>
                <strong>{getTranslatedFieldName(fieldName)}:</strong>
                {renderFieldValue(fieldName, combination[fieldName])}
              </div>
            ))}
          </div>
        </StackView>
      </React.Fragment>
    );
  };

  // Helper function to render unknown fields (fields not in any category)
  const renderUnknownFields = () => {
    const knownFields = Object.values(fieldCategories).flatMap(category => category.fields);
    const unknownFields = Object.keys(combination).filter(field => 
      !knownFields.includes(field) && 
      field !== 'id' && // Exclude internal fields
      field !== 'createdAt' && // Exclude internal fields
      field !== 'apiResponse' // Exclude API response object
    );
    
    if (unknownFields.length === 0) return null;

    return (
      <React.Fragment key="unknownFields">
        <Divider />
        <StackView space={3}>
          <Typography heading="h4" variant={{ size: 'h4' }} block>Additional Information</Typography>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {unknownFields.map(fieldName => (
              <div key={fieldName}>
                <strong>{getTranslatedFieldName(fieldName)}:</strong>
                {renderFieldValue(fieldName, combination[fieldName])}
              </div>
            ))}
          </div>
        </StackView>
      </React.Fragment>
    );
  };

  const modalContent = (
    <Backdrop onClick={onClose}>
      <ModalMain onClick={(e) => e.stopPropagation()}>
        <ModalInner>
          <ModalMessageMain>
            <ModalHeading>
              <NavbarPopup>
                <ModalHeadingStyled>
                  Coupon Details
                </ModalHeadingStyled>
              </NavbarPopup>
            </ModalHeading>
            <div style={{width:"100%", padding:"3rem", overflow:"auto", height:"100%", maxHeight:"100%"}}>
              <Divider space={1} variant={{ inverse: true }} />
              
              <StackView space={4}>
                {/* Dynamically render all field categories */}
                {Object.keys(fieldCategories).map(categoryKey => 
                  renderCategorySection(categoryKey)
                )}
                
                {/* Render any unknown fields */}
                {renderUnknownFields()}
              </StackView>
            </div>
          </ModalMessageMain>

          <ModalCancel>
            <ModalButton onClick={onClose}
              style={{color: "white", borderRadius: "50%", border: " 1px solid white",
              height: "27px", width: "27px", fontSize: "16px", background: "rgb(75, 40, 109)", marginTop: "4px",display:"flex",justifyContent:"center",
              alignItems:"center"}}
              >
              ×
            </ModalButton>
          </ModalCancel>
        </ModalInner>
      </ModalMain>
    </Backdrop>
  );

  return createPortal(modalContent, document.body);
};

CouponDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  combination: PropTypes.shape({}),
  referenceOptions: PropTypes.shape({
    channels: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
    dealerships: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }))
  }),
  onClose: PropTypes.func.isRequired
};

export default CouponDetailsModal;
